const NOT_PROVIDED = "Not provided";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Sentence-case a stored status or priority token.
 * @param {unknown} value
 * @returns {string}
 */
export function sentenceLabel(value) {
  const text = String(value || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase();
  if (!text) return NOT_PROVIDED;
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Format a recorded money or rate value the way the workspace shows it.
 * @param {unknown} value
 * @returns {string}
 */
export function formatRecordedAmount(value) {
  const number = Number(value);
  const safe = Number.isFinite(number) ? number : 0;
  return safe.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format an instant in UTC, matching the workspace date style.
 * @param {unknown} value
 * @param {{ withZone?: boolean }} [options]
 * @returns {string}
 */
export function formatWorkspaceTime(value, options = {}) {
  if (value == null || value === "") return NOT_PROVIDED;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const stamp = `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}, ${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}`;
  return options.withZone === false ? stamp : `${stamp} UTC`;
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
function isObjectId(value) {
  return typeof value === "string" && /^[a-f0-9]{24}$/i.test(value);
}

/**
 * @param {unknown} record
 * @returns {string}
 */
function personName(record) {
  if (!record || typeof record !== "object") return "";
  if (record.fullName) return String(record.fullName);
  const joined = [record.firstName, record.lastName].filter(Boolean).join(" ");
  if (joined) return joined;
  if (record.name) return String(record.name);
  return "";
}

/**
 * @param {unknown} record
 * @returns {string}
 */
function referenceId(record) {
  if (!record) return "";
  if (typeof record === "string") return isObjectId(record) ? record : "";
  const id = record._id || record.id;
  return id && isObjectId(String(id)) ? String(id) : "";
}

/**
 * Route for a person or record that has its own page.
 * @param {string} kind
 * @param {unknown} record
 * @returns {string}
 */
function referencePath(kind, record) {
  const id = referenceId(record);
  if (!id) return "";
  if (kind === "driver") return `/drivers/${id}`;
  if (kind === "client") return `/clients/${id}`;
  if (kind === "invoice") return `/invoices/${id}`;
  return "";
}

/**
 * A linked person or company that may only be an id.
 * @param {unknown} record
 * @param {string} referencedTitle
 * @param {string} missingDetail
 * @param {string} [kind] driver, client, or invoice when that record has a page
 * @returns {{ title: string, detail: string, to: string }}
 */
function referenceRecord(record, referencedTitle, missingDetail, kind = "") {
  const to = referencePath(kind, record);
  if (!record) return { title: NOT_PROVIDED, detail: "", to };
  if (typeof record === "string") {
    return isObjectId(record)
      ? { title: referencedTitle, detail: missingDetail, to }
      : { title: record, detail: "", to };
  }
  const name = personName(record);
  if (name) return { title: name, detail: "", to };
  const invoiceNumber = record.invoiceId || record.number;
  if (invoiceNumber && !isObjectId(invoiceNumber)) {
    return { title: String(invoiceNumber), detail: "", to };
  }
  return { title: referencedTitle, detail: missingDetail, to };
}

/**
 * @param {unknown} place
 * @returns {string}
 */
function placeLabel(place) {
  if (!place) return "";
  if (typeof place === "string") return isObjectId(place) ? "" : place;
  const address = place.address && typeof place.address === "object" ? place.address : place;
  return (
    address.fullAddress ||
    [address.city, address.state].filter(Boolean).join(", ") ||
    place.name ||
    ""
  );
}

/**
 * @param {object|null|undefined} location
 * @returns {{ name: string, detail: string }}
 */
function locationParts(location) {
  if (!location) return { name: NOT_PROVIDED, detail: "" };
  if (typeof location === "string") {
    return { name: isObjectId(location) ? NOT_PROVIDED : location, detail: "" };
  }
  const address = location.address && typeof location.address === "object" ? location.address : null;
  const name =
    location.name ||
    location.label ||
    address?.fullAddress ||
    location.fullAddress ||
    NOT_PROVIDED;
  const detail = [
    location.city || address?.city,
    location.state || address?.state,
  ]
    .filter(Boolean)
    .join(", ");
  if (detail && detail !== name) return { name, detail };
  return { name, detail: "" };
}

/**
 * @param {object|null|undefined} leg
 * @returns {string}
 */
function legRouteText(leg) {
  const route = leg?.route;
  if (!route || typeof route !== "object") return "";
  const origin = placeLabel(route.origin);
  const destination = placeLabel(route.destination);
  if (origin && destination) return `${origin} → ${destination}`;
  return origin || destination || "";
}

/**
 * @param {object|null|undefined} job
 * @returns {string}
 */
function customerReference(job) {
  const metadata = job?.metadata || {};
  const fromJob =
    metadata.customerReference ||
    metadata.customerPO ||
    metadata.poNumber ||
    metadata.reference;
  if (fromJob) return String(fromJob);
  const legs = Array.isArray(job?.legs) ? job.legs : [];
  for (const leg of legs) {
    const shipping = leg?.shipping || {};
    const listed = Array.isArray(shipping.customerReferenceNumbers)
      ? shipping.customerReferenceNumbers.find(Boolean)
      : "";
    if (listed) return String(listed);
    if (shipping.shipperReference) return String(shipping.shipperReference);
  }
  return NOT_PROVIDED;
}

/**
 * @param {object|null|undefined} job
 * @returns {string}
 */
function commodityLabel(job) {
  if (job?.metadata?.commodity) return String(job.metadata.commodity);
  const legs = Array.isArray(job?.legs) ? job.legs : [];
  for (const leg of legs) {
    if (leg?.shipping?.commodity) return String(leg.shipping.commodity);
    if (leg?.shipping?.description) return String(leg.shipping.description);
  }
  return NOT_PROVIDED;
}

/**
 * @param {object|null|undefined} job
 * @param {string} [companyLabel]
 * @returns {string}
 */
function companyName(job, companyLabel) {
  const parent = job?.parentCompany;
  if (parent && typeof parent === "object") {
    return parent.slug || parent.name || companyLabel || NOT_PROVIDED;
  }
  if (typeof parent === "string" && !isObjectId(parent)) return parent;
  return companyLabel || NOT_PROVIDED;
}

/**
 * @param {unknown} priority
 * @returns {string}
 */
function priorityLabel(priority) {
  if (!priority) return "";
  const text = sentenceLabel(priority);
  if (text === NOT_PROVIDED) return "";
  if (/priority/i.test(text)) return text;
  return `${text} priority`;
}

/**
 * @param {number} count
 * @returns {string}
 */
export function unresolvedLegCopy(count) {
  const noun = count === 1 ? "reference" : "references";
  return `${count} unresolved leg ${noun}. Leg details are not included in this job record.`;
}

/**
 * Map a job record into the workspace view.
 * Missing fields stay visible as "Not provided" so the layout does not collapse.
 *
 * @param {object|null|undefined} job
 * @param {{ companyLabel?: string, legsTitle?: string }} [options]
 * @returns {object}
 */
export function buildJobWorkspace(job, options = {}) {
  const record = job || {};
  const legs = (Array.isArray(record.legs) ? record.legs : []).filter(Boolean);
  const loadNumber = record.loadNumber ? String(record.loadNumber) : "";
  const location = locationParts(record.location);
  const drivers = Array.isArray(record.drivers) ? record.drivers.filter(Boolean) : [];
  const assignees = Array.isArray(record.assignedTo) ? record.assignedTo.filter(Boolean) : [];
  const priority = priorityLabel(record.priority);
  const otherTotals =
    Number(record.serviceFeeTotal || 0) +
    Number(record.partsTotal || 0) +
    Number(record.hourlyTotal || 0);

  const legRows = legs.map((leg, index) => {
    const routeText = legRouteText(leg);
    const driver = referenceRecord(leg.driver, "Referenced driver", "Name not provided");
    const driverName = driver.detail ? "" : driver.title === NOT_PROVIDED ? "" : driver.title;
    const status = leg.status ? sentenceLabel(leg.status) : NOT_PROVIDED;
    const letter = String.fromCharCode(65 + (index % 26));
    return {
      id: leg._id || `leg-${index}`,
      label: leg.loadType || leg.orderNumber || `Linked leg ${letter}`,
      loadNumber: leg.loadNumber || loadNumber || NOT_PROVIDED,
      route: routeText || NOT_PROVIDED,
      routeDetail: routeText ? "" : "Origin / destination",
      driver: driverName || NOT_PROVIDED,
      status,
      unresolved: !routeText && !driverName,
    };
  });

  const unresolvedLegCount = legRows.filter((leg) => leg.unresolved).length;
  const approval = record.approval || {};
  const approvalStatus = approval.status || "pending";

  return {
    id: record._id || "",
    jobNumber: record.jobId || loadNumber || NOT_PROVIDED,
    statusId: record.status || "",
    statusLabel: record.status ? sentenceLabel(record.status) : NOT_PROVIDED,
    priorityLabel: priority,
    priorityTone: /high|urgent/i.test(priority) ? "high" : "default",
    loadLabel: loadNumber ? `Load ${loadNumber}` : "Load not provided",
    companyLabel: companyName(record, options.companyLabel),
    active: record.active !== false,
    summary: [
      { label: "Customer reference", value: customerReference(record), detail: "" },
      { label: "Commodity", value: commodityLabel(record), detail: "" },
      { label: "Job location", value: location.name, detail: location.detail },
      {
        label: "Appointment · UTC",
        value: formatWorkspaceTime(record.appointmentDate, { withZone: false }),
        detail: "",
      },
    ],
    legsTitle: options.legsTitle || "Loads & legs",
    legs: legRows,
    unresolvedLegCount,
    unresolvedCopy:
      unresolvedLegCount > 0 ? unresolvedLegCopy(unresolvedLegCount) : "",
    dispatchNote: record.note ? String(record.note) : NOT_PROVIDED,
    schedule: [
      { label: "Original call", value: formatWorkspaceTime(record.originalCall) },
      { label: "Transaction date", value: formatWorkspaceTime(record.transactionDate) },
      { label: "Appointment", value: formatWorkspaceTime(record.appointmentDate) },
    ],
    people: [
      {
        label: "Driver",
        ...referenceRecord(
          drivers[0] || null,
          "Referenced driver",
          "Name not provided",
          "driver"
        ),
      },
      {
        label: "Assignee",
        ...referenceRecord(assignees[0] || null, "Referenced assignee", "Name not provided"),
      },
      {
        label: "Client",
        ...referenceRecord(record.client, "Referenced client", "Name not provided", "client"),
      },
      {
        label: "Invoice",
        ...referenceRecord(
          record.invoiceId,
          "Referenced invoice",
          "Number not provided",
          "invoice"
        ),
      },
    ],
    complianceLabel: record.defaultComplianceProfile
      ? `Compliance profile: ${record.defaultComplianceProfile}`
      : "Compliance profile: not provided",
    aiCreatedLabel: `AI-created: ${record.aiCreated ? "Yes" : "No"}`,
    finance: {
      total: formatRecordedAmount(
        record.calculatedTotalCharges ?? record.totalCharges
      ),
      currencyLabel: record.currency || record.metadata?.currency
        ? String(record.currency || record.metadata.currency)
        : "Currency not specified",
      amounts: [
        { label: "Base", value: formatRecordedAmount(record.base ?? record.totalBaseCharges) },
        { label: "Accessories total", value: formatRecordedAmount(record.accessoriesTotal) },
        { label: "Taxes", value: formatRecordedAmount(record.salesTax) },
        { label: "Other supplied totals", value: formatRecordedAmount(otherTotals) },
      ],
      rateMode: record.useGlobalRates ? "Global rates" : "Job rates",
      rates: [
        { label: "Base rate", value: formatRecordedAmount(record.baseRate) },
        { label: "Fuel rate", value: formatRecordedAmount(record.fuelRate) },
        {
          label: record.useFlatRate ? "Flat rate" : "Flat rate · not used",
          value: formatRecordedAmount(record.flatRate),
        },
      ],
    },
    approval: {
      status: approvalStatus,
      label: sentenceLabel(approvalStatus),
      reviewedLabel: formatWorkspaceTime(approval.reviewedAt),
      note: approval.notes ? String(approval.notes) : "",
    },
  };
}
